/**
 * The minimum an interaction has to expose for us to tell whether it was an email and what its
 * subject was. Deliberately structural: the dashboard's `HistoryData` and the extension sidepanel's
 * `HistoryItem` describe the same rows with different wrappers, and both satisfy this.
 */
export type EmailSubjectInteraction = {
  interactionAction?: string | null;
  subject?: string | null;
  /** Owner of the interaction. The contact timeline is team-wide; threading is per-user. */
  userId?: string | null;
};

/**
 * The email thread a composer can continue: it exists (that is the whole point of a non-null value)
 * and it either has a subject for the reply to inherit or it does not.
 *
 * Deliberately ONE object rather than a `hasPreviousEmail` boolean beside a `previousEmailSubject`
 * string. Those were two props for two halves of one fact, and nothing stopped a call site from
 * passing the subject while forgetting the gate — which compiles clean and silently removes the
 * "Reply in thread" toggle, the exact bug this shape exists to prevent.
 */
export type PreviousEmailThread = {
  /**
   * Subject the reply will inherit, or null when that email has none to give. Null does NOT mean
   * "no thread" — the thread is still threadable (threading rides on the provider ids the server
   * stored, never on the subject); it means the composer keeps its Subject field instead of
   * replacing it with the thread banner.
   */
  subject: string | null;
};

/**
 * The timeline actions that put a message in a CHANNEL — a local, flat mirror of the message rows of
 * the DB's `contact_interaction_action` enum. Local by rule: UI components may not import `pg-gen`
 * types (see `ContactDetail/types.ts`), so this is the seam where those strings are named once and
 * both sets below are checked against it, rather than three bare string arrays drifting apart.
 */
type ChannelAction =
  | 'connection_sent'
  | 'connection_accepted'
  | 'message_sent'
  | 'message_received'
  | 'inmail_sent'
  | 'inmail_received'
  | 'inmail_declined'
  | 'email_sent'
  | 'email_received'
  | 'phone_call_sent'
  | 'phone_received';

// Typed through `ChannelAction` so a typo is a compile error, and so the email actions are provably
// a SUBSET of the channel actions — `wasLastStepEmail` only ever asks this about a row that already
// passed the channel gate, so an email action missing from that gate would silently answer `false`.
const EMAIL_ACTIONS: ReadonlySet<string> = new Set<ChannelAction>(['email_sent', 'email_received']);

/**
 * Flatten a stored subject to the plain text a mail client will show.
 *
 * Mirrors the server's `convertHtmlToText` (`apps/../api/utils/utils.ts`) EXACTLY — bare
 * `textContent`, nothing else — because that is what `buildEmailReplyOptions` runs the inherited
 * subject through before putting it on the wire. Notably it does NOT use this package's
 * `getTextFromHtml`, whose `/[^\x20-\x7E]+/g` strip is right for body previews and wrong here: it
 * deletes every accent, curly quote, em dash and emoji, so `Re: Café ☕ chat` rendered in the banner
 * as `Re: Caf  chat` while the send went out with the real subject — and an all-non-ASCII subject
 * flattened to empty, which used to hide the toggle outright and now would wrongly promise the rep
 * that the subject they type is the one that gets used.
 *
 * Subjects are stored as plain text on send, but rows that round-tripped through the editor can
 * still carry `<p>`-wrapped HTML, which is why this runs at all.
 */
function flattenSubject(subject: string): string {
  return new DOMParser().parseFromString(subject, 'text/html').body.textContent ?? '';
}

/**
 * The email thread THE CURRENT USER can continue with a contact, or null when there is none.
 *
 * Non-null is what makes the composer's "Reply in thread" toggle available, and `subject` is what
 * fills its banner. Presence of the email is the gate, NOT its subject: threading rides on the
 * provider ids the server stored, so a thread whose latest email has no subject is still perfectly
 * threadable — it just leaves the composer's Subject field up instead of showing the banner. Gating
 * on the subject (which this helper used to do, as `getPreviousEmailSubject`) meant a subject-less
 * email hid the toggle entirely: the rep could see the email sitting on the timeline with no way to
 * reply into it.
 *
 * It must agree with what the server actually threads onto: `contactUserInteractionGetLastEmail`,
 * which takes the latest `email_sent`/`email_received` row **scoped to `user_id`**.
 *
 * The `userId` scoping is load-bearing, not defensive. The contact timeline both surfaces render is
 * TEAM-wide (`contactInteractionFetch` has no user predicate), and threading is per-user — for
 * `gmail_api` BOTH stored ids (`provider_email_id` is the Gmail message id, `provider_thread_id` the
 * Gmail thread id) belong to one mailbox. So a teammate's email is visible on the timeline but is
 * not something this rep's mailbox can reply into, and offering the toggle for it would name a
 * thread the send could never land in.
 *
 * Note this is NOT the same set as the removed "Reply to existing thread" subject dropdown, which
 * read `email_sent` only — it couldn't see the inbound message being replied to.
 *
 * Known gap, unchanged by this helper: both surfaces feed it a PAGE of the timeline (50 rows on the
 * dashboard, 20 in the sidepanel), so an email buried under that many newer rows is invisible here
 * while `contactUserInteractionGetLastEmail` — which has no limit — would still thread onto it.
 * Measured at 12 of 1851 contacts, so the fix (a dedicated "last email" query) is not worth a
 * round-trip on this path yet.
 *
 * @param interactions Timeline rows in ascending date order (oldest first), as both surfaces render them.
 * @param currentUserId The signed-in user. Rows belonging to anyone else are skipped.
 */
export function getPreviousEmailThread(
  interactions: readonly EmailSubjectInteraction[] | null | undefined,
  currentUserId: string | null | undefined,
): PreviousEmailThread | null {
  if (interactions == null || currentUserId == null) return null;
  for (let i = interactions.length - 1; i >= 0; i--) {
    const interaction = interactions[i];
    if (interaction?.interactionAction == null || !EMAIL_ACTIONS.has(interaction.interactionAction)) continue;
    if (interaction.userId !== currentUserId) continue;
    // Stop at the FIRST email found scanning backwards — never skip past a subject-less one to an
    // older email. The server always threads onto the latest email, so falling back to an earlier
    // subject would show the rep one thread while the send landed in another.
    const subject = flattenSubject(interaction.subject ?? '').trim();
    return { subject: subject.length > 0 ? subject : null };
  }
  return null;
}

/**
 * Every {@link ChannelAction}, as the set `wasLastStepEmail` gates on.
 *
 * Deliberately narrower than "a step ran". Passive campaign steps (`visit`, `follow`, `like_posts`,
 * `comment_on_post`) are automation that happens *to* the contact without addressing them, and a
 * campaign firing an overnight profile visit must not make the rep's own email exchange look stale —
 * that would flip the default off for exactly the contacts this feature exists to serve. Same for
 * pure bookkeeping (`stage_changed`, `cadence_start`, `document_viewed`, `step_skipped`).
 */
const CHANNEL_ACTIONS: ReadonlySet<string> = new Set<ChannelAction>([
  'connection_sent',
  'connection_accepted',
  'message_sent',
  'message_received',
  'inmail_sent',
  'inmail_received',
  'inmail_declined',
  'email_sent',
  'email_received',
  'phone_call_sent',
  'phone_received',
]);

/**
 * Was the last step this user ran on (or received from) this contact an email?
 *
 * This is the composer's default for "Reply in thread": when the most recent message either way was
 * an email, the rep opening the email quick action is continuing that conversation ~every time, so
 * the toggle starts ON. When the conversation has since moved to LinkedIn — or to any other channel
 * in {@link CHANNEL_ACTIONS} — a fresh thread is the safer default and the rep can still opt in.
 *
 * "Step" here means a message in a channel, not any row on the timeline: see {@link CHANNEL_ACTIONS}
 * for why passive automation and bookkeeping rows are scanned past rather than treated as the last
 * thing that happened.
 *
 * Kept separate from {@link getPreviousEmailThread} rather than folded into one pass: the two scan
 * for different things (the newest EMAIL vs the newest message in ANY channel) and stop at different
 * rows, so one loop serving both would need two independent "still looking" flags to stay correct.
 * Two short backward scans over one page of timeline is the cheaper trade.
 *
 * Scoped to `currentUserId` for the same reason as {@link getPreviousEmailThread}: the timeline is
 * team-wide but threading is per-user, so a teammate's LinkedIn message must not decide what the
 * current rep's email defaults to.
 *
 * @param interactions Timeline rows in ascending date order (oldest first), as both surfaces render them.
 * @param currentUserId The signed-in user. Rows belonging to anyone else are skipped.
 */
export function wasLastStepEmail(
  interactions: readonly EmailSubjectInteraction[] | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (interactions == null || currentUserId == null) return false;
  for (let i = interactions.length - 1; i >= 0; i--) {
    const interaction = interactions[i];
    if (interaction == null || interaction.userId !== currentUserId) continue;
    const action = interaction.interactionAction;
    if (action == null || !CHANNEL_ACTIONS.has(action)) continue;
    return EMAIL_ACTIONS.has(action);
  }
  return false;
}
